/**
 * Relatórios de e-mail — cliente de /api/v1/reports/email/* (ms-api-reports, mesmo Bearer).
 *
 * Permissão `email.reports.view`: sem ela a API responde 403 em todas as rotas.
 * Atenção: o interceptor do `reportsClient` desloga em QUALQUER 401, inclusive o desta API.
 * A resposta é cacheada 60 s pelo servidor (10 s quando degradada) — `staleTime: 60_000` no cliente.
 */
import axios from 'axios';
import reportsClient from './reports-client';
import type {
    BroadcastsResponse, ClicksResponse, EmailPeriodParams, FiltersResponse, FunnelsResponse,
    LeadsResponse, OffersResponse, OverviewResponse, PreferencesResponse, RoutesResponse,
    TemplatesResponse,
} from '@/types/email-v1';

const BASE = '/api/v1/reports/email';

/** Erro já lido: `detail` é a mensagem pronta em português; `missing` só vem no 503 de carga pendente. */
export class EmailApiError extends Error {
    constructor(
        readonly status: number,
        readonly detail: string,
        readonly missing: string[] = [],
    ) {
        super(detail);
        this.name = 'EmailApiError';
    }
    /** 503: o período ainda não foi carregado — é espera, não defeito. */
    get isPending() { return this.status === 503; }
    /** 403: sem `email.reports.view`, ou projeto fora do acesso. */
    get isForbidden() { return this.status === 403; }
}

const FALLBACK: Record<number, string> = {
    400: 'Período inválido.',
    403: 'Você não tem acesso aos relatórios de e-mail.',
    503: 'Os dados deste período ainda não foram carregados',
};

const asEmailError = (e: unknown): EmailApiError => {
    if (axios.isAxiosError(e)) {
        const status = e.response?.status ?? 0;
        const body = e.response?.data as { detail?: unknown; missing?: unknown } | undefined;
        const detail = typeof body?.detail === 'string' && body.detail
            ? body.detail
            : FALLBACK[status] || 'Não foi possível carregar os relatórios de e-mail.';
        const missing = Array.isArray(body?.missing) ? body.missing.filter((x): x is string => typeof x === 'string') : [];
        return new EmailApiError(status, detail, missing);
    }
    return new EmailApiError(0, 'Não foi possível carregar os relatórios de e-mail.');
};

const get = async <T,>(path: string, params: object): Promise<T> => {
    try {
        const { data } = await reportsClient.get<T>(BASE + path, { params });
        return data;
    } catch (e) {
        throw asEmailError(e);
    }
};

export const getEmailOverview = (p: EmailPeriodParams) => get<OverviewResponse>('/overview', p);
export const getEmailBroadcasts = (p: EmailPeriodParams) => get<BroadcastsResponse>('/broadcasts', p);
export const getEmailFunnels = (p: EmailPeriodParams) => get<FunnelsResponse>('/funnels', p);
export const getEmailTemplates = (p: EmailPeriodParams) => get<TemplatesResponse>('/templates', p);
export const getEmailRoutes = (p: EmailPeriodParams) => get<RoutesResponse>('/routes', p);
export const getEmailOffers = (p: EmailPeriodParams) => get<OffersResponse>('/offers', p);
export const getEmailLeads = (p: EmailPeriodParams) => get<LeadsResponse>('/leads', p);
export const getEmailClicks = (p: EmailPeriodParams) => get<ClicksResponse>('/clicks', p);

/** Em /filters `project_id` é opcional, mas vazio (`project_id=`) é 400 — por isso só vai quando tem valor. */
export const getEmailFilters = (projectId?: string) =>
    get<FiltersResponse>('/filters', projectId ? { project_id: projectId } : {});

export const getEmailPrefs = () => get<PreferencesResponse>('/preferences', {});

export const putEmailPrefs = async (prefs: Record<string, unknown>): Promise<void> => {
    try {
        await reportsClient.put(`${BASE}/preferences`, { prefs });
    } catch (e) {
        throw asEmailError(e);
    }
};
